import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Slider } from '@/components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api } from '@/lib/api';
import type { InventoryItem, Ration } from '@/lib/schemas';

interface RationWorkshopProps {
  rations: Ration[];
  inventory: InventoryItem[];
}

const GROUP_LABELS: Record<string, string> = {
  milking: 'Milking',
  dry: 'Dry',
  heifers: 'Heifers',
};

interface RationImpact {
  batchWeightChange: number;
  dmIntakeChange: number | null;
}

function computeImpact(ration: Ration, proposed: Record<string, number>): RationImpact {
  const headCount = ration.head_count_at_creation ?? 100;
  const batchHours = ration.batch_hours;

  let batchWeightChange = 0;
  let dmChange: number | null = 0;
  let hasDm = false;

  for (const ing of ration.ingredients) {
    const currentLbs = ing.lbs_per_head_day;
    const newLbs = proposed[ing.name] ?? currentLbs;
    const diff = newLbs - currentLbs;
    batchWeightChange += diff * headCount * (batchHours / 24);

    if (ing.dm_pct != null) {
      hasDm = true;
      dmChange = (dmChange ?? 0) + diff * (ing.dm_pct / 100);
    }
  }

  return {
    batchWeightChange,
    dmIntakeChange: hasDm ? dmChange : null,
  };
}

export function RationWorkshop({ rations, inventory: _inventory }: RationWorkshopProps) {
  const firstGroupId = rations[0]?.group_id ?? 'milking';
  const [activeGroup, setActiveGroup] = useState(firstGroupId);
  const [proposed, setProposed] = useState<Record<string, Record<string, number>>>({});
  const [preview, setPreview] = useState<object | null>(null);
  const [confirmToken, setConfirmToken] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const activeRation = rations.find((r) => r.group_id === activeGroup);

  function getProposedValue(group: string, name: string, currentValue: number): number {
    return proposed[group]?.[name] ?? currentValue;
  }

  function updateIngredient(group: string, name: string, value: number) {
    setProposed((prev) => ({
      ...prev,
      [group]: { ...prev[group], [name]: value },
    }));
  }

  const saveMutation = useMutation({
    mutationFn: async (confirmTok?: string) => {
      if (!activeRation) return;
      const changes = activeRation.ingredients.map((ing) => ({
        name: ing.name,
        lbs_per_head_day: getProposedValue(activeGroup, ing.name, ing.lbs_per_head_day),
        ...(ing.dm_pct != null && { dm_pct: ing.dm_pct }),
      }));
      return api.post('/api/ration/update', {
        group_id: activeGroup,
        changes,
        ...(confirmTok && { confirm_token: confirmTok }),
      });
    },
    onSuccess: (data: unknown) => {
      const result = data as { status?: string; preview?: unknown; confirm_token?: string };
      if (result?.status === 'preview') {
        setPreview(result.preview as object);
        setConfirmToken(result.confirm_token ?? '');
        setConfirmOpen(true);
      } else {
        setConfirmOpen(false);
        setPreview(null);
        setConfirmToken('');
        setProposed((prev) => ({ ...prev, [activeGroup]: {} }));
      }
    },
  });

  if (!activeRation) {
    return <p className="text-ndd-400">No ration data available</p>;
  }

  const hasChanges = Object.values(proposed[activeGroup] ?? {}).some((v, i) => {
    const ing = activeRation.ingredients[i];
    return ing && v !== ing.lbs_per_head_day;
  });

  return (
    <div className="space-y-4">
      {/* Group selector */}
      <Tabs value={activeGroup} onValueChange={setActiveGroup}>
        <TabsList>
          {rations.map((r) => (
            <TabsTrigger key={r.group_id} value={r.group_id}>
              {GROUP_LABELS[r.group_id] ?? r.group_id}
            </TabsTrigger>
          ))}
        </TabsList>

        {rations.map((ration) => {
          const groupProposed = proposed[ration.group_id] ?? {};
          const groupImpact = computeImpact(ration, groupProposed);

          return (
            <TabsContent key={ration.group_id} value={ration.group_id}>
              <div className="grid grid-cols-2 gap-6">
                {/* Left: Current ration */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-ndd-300">Current Ration</h3>
                  <div className="rounded-lg bg-ndd-900 overflow-hidden">
                    <div className="px-4 py-2 border-b border-ndd-800 grid grid-cols-3 text-xs font-semibold text-ndd-400">
                      <span>Ingredient</span>
                      <span>lbs/hd</span>
                      <span>DM%</span>
                    </div>
                    {ration.ingredients.map((ing) => (
                      <div
                        key={ing.name}
                        className="px-4 py-2 grid grid-cols-3 text-sm border-b border-ndd-800/50 last:border-0"
                      >
                        <span className="text-white">{ing.name}</span>
                        <span className="text-ndd-300 font-mono">
                          {ing.lbs_per_head_day.toFixed(1)}
                        </span>
                        <span className="text-ndd-500">
                          {ing.dm_pct != null ? `${ing.dm_pct}%` : '—'}
                        </span>
                      </div>
                    ))}
                    <div className="px-4 py-2 grid grid-cols-3 text-sm font-semibold">
                      <span className="text-ndd-400">Total</span>
                      <span className="text-white font-mono">
                        {ration.total_lbs_per_head?.toFixed(1) ?? '—'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Right: Workshop sliders */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-ndd-300">Proposed Changes</h3>
                  <div className="space-y-4">
                    {ration.ingredients.map((ing) => {
                      const currentVal = ing.lbs_per_head_day;
                      const proposedVal = groupProposed[ing.name] ?? currentVal;
                      const changed = proposedVal !== currentVal;
                      return (
                        <div key={ing.name} className="space-y-1.5">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-white">{ing.name}</span>
                            <span
                              className={`font-mono font-semibold ${changed ? 'text-ndd-400' : 'text-ndd-500'}`}
                            >
                              {proposedVal.toFixed(1)} lbs/hd
                              {changed && (
                                <span className="ml-2 text-xs">
                                  ({proposedVal > currentVal ? '+' : ''}
                                  {(proposedVal - currentVal).toFixed(1)})
                                </span>
                              )}
                            </span>
                          </div>
                          <Slider
                            min={0}
                            max={currentVal * 2}
                            step={0.1}
                            value={[proposedVal]}
                            onValueChange={([v]) =>
                              updateIngredient(ration.group_id, ing.name, v ?? currentVal)
                            }
                          />
                        </div>
                      );
                    })}
                  </div>

                  {/* Impact summary */}
                  {ration.group_id === activeGroup && (
                    <div className="rounded-lg bg-ndd-900/50 px-4 py-3 space-y-1 text-sm">
                      <p className="text-xs font-semibold text-ndd-400 mb-2">Impact</p>
                      <div className="flex justify-between">
                        <span className="text-ndd-400">Batch weight change</span>
                        <span
                          className={
                            groupImpact.batchWeightChange > 0
                              ? 'text-ndd-300'
                              : groupImpact.batchWeightChange < 0
                                ? 'text-amber-300'
                                : 'text-white'
                          }
                        >
                          {groupImpact.batchWeightChange > 0 ? '+' : ''}
                          {Math.round(groupImpact.batchWeightChange).toLocaleString()} lbs
                        </span>
                      </div>
                      {groupImpact.dmIntakeChange != null && (
                        <div className="flex justify-between">
                          <span className="text-ndd-400">DM intake change</span>
                          <span className="text-white">
                            {groupImpact.dmIntakeChange > 0 ? '+' : ''}
                            {groupImpact.dmIntakeChange.toFixed(2)} lbs/hd
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
          );
        })}
      </Tabs>

      {/* Save button */}
      <div className="flex justify-end gap-3">
        <Button
          variant="outline"
          onClick={() => setProposed((prev) => ({ ...prev, [activeGroup]: {} }))}
        >
          Reset
        </Button>
        <Button
          onClick={() => saveMutation.mutate(undefined)}
          disabled={!hasChanges || saveMutation.isPending}
        >
          Save Changes
        </Button>
      </div>

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Ration Update</DialogTitle>
            <DialogDescription>Review the proposed changes before saving.</DialogDescription>
          </DialogHeader>
          {preview && (
            <pre className="rounded-lg bg-ndd-900/50 p-3 text-xs text-ndd-300 overflow-auto max-h-48">
              {JSON.stringify(preview, null, 2)}
            </pre>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => saveMutation.mutate(confirmToken)}
              disabled={saveMutation.isPending}
            >
              Confirm Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
